/**
 * Created: 2026-05-10
 * Initial commit: Add coupon issue page (feat/coupon/mr-6)
 *
 * Page for issuing coupons to users. Shows user search, user coupon history,
 * and an issue button to issue a new coupon from a selected template.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { currency, dateFormat, message } from '@homemaster/jslib'
import AddIcon from '@mui/icons-material/Add'
import BlockIcon from '@mui/icons-material/Block'
import QrCodeIcon from '@mui/icons-material/QrCode'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import { useState } from 'react'
import CouponPosterDialog from '../../components/CouponPosterDialog'
import OrderDialog from '../../components/OrderDialog'
import UserSelector, { type UserSelectorUser } from '../../components/UserSelector'
import {
  CouponStatus,
  CouponTemplatesDocument,
  UserCouponsDocument,
  VoidCouponDocument,
} from '../../gql/graphql'
import { useSiteContext } from '../../hooks'
import { COUPON_CATEGORY_LABEL, SPORT_LABEL } from '../../lib/domainLabels'
import IssueCouponDialog from './IssueCouponDialog'

// Created: 2026-05-10 | Add coupon issue page queries and mutations
gql`
  query UserCoupons($siteId: UUID!, $userId: UUID) {
    userCoupons(siteId: $siteId, userId: $userId) {
      id
      status
      issuedAt
      expiresAt
      discountedAmount
      redeemedAt
      orderId
      issuer {
        id
        openid
        name
        avatar
        gender
      }
      template {
        id
        name
        category
        sport
        discountAmount
      }
    }
  }

  mutation IssueCoupon($couponTemplateId: ID!, $userId: ID, $validDays: Int, $quantity: Int) {
    issueCoupon(couponTemplateId: $couponTemplateId, userId: $userId, validDays: $validDays, quantity: $quantity) {
      ... on GraphqlSuccess {
        __typename
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }

  mutation VoidCoupon($couponId: ID!) {
    voidCoupon(couponId: $couponId) {
      ... on GraphqlSuccess {
        __typename
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

const STATUS_LABEL: Record<CouponStatus, string> = {
  [CouponStatus.Active]: '有效',
  [CouponStatus.Used]: '已使用',
  [CouponStatus.Voided]: '已作废',
  [CouponStatus.Expired]: '已过期',
}

const columns: GridColDef[] = [
  {
    field: 'couponId',
    headerName: '券ID',
    width: 120,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'templateName',
    headerName: '模板名称',
    width: 200,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'category',
    headerName: '类型',
    width: 100,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'sport',
    headerName: '运动',
    width: 80,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'status',
    headerName: '状态',
    width: 80,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'issuedAt',
    headerName: '发放时间',
    width: 160,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'issuerName',
    headerName: '发放人',
    width: 120,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'expiresAt',
    headerName: '到期日',
    width: 110,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'discountedAmount',
    headerName: '抵扣金额',
    width: 100,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'redeemedAt',
    headerName: '使用时间',
    width: 160,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'actions',
    headerName: '',
    width: 80,
    sortable: false,
    align: 'center',
    renderCell: (params) => {
      if (params.row.rawStatus === CouponStatus.Used && params.row.orderId) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 0.5 }}>
            <Tooltip title="查看订单">
              <IconButton
                size="small"
                color="primary"
                onClick={() => params.row.onViewOrder(params.row.orderId as string)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )
      }
      if (params.row.rawStatus !== CouponStatus.Active) return null
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 0.5 }}>
          {params.row.isUnbound && (
            <Tooltip title="下载海报">
              <IconButton
                size="small"
                color="primary"
                onClick={() => params.row.onPoster(params.row.id as string)}
              >
                <QrCodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="作废">
            <IconButton
              size="small"
              color="error"
              onClick={() => params.row.onVoid(params.row.id as string)}
            >
              <BlockIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
  },
]

export default function UserCouponsPage() {
  const { currentSite } = useSiteContext()
  const siteId = currentSite?.id as string

  const [selectedUser, setSelectedUser] = useState<UserSelectorUser | null>(null)
  const [searched, setSearched] = useState(false)
  const [issueDialogOpen, setIssueDialogOpen] = useState(false)
  const [posterCouponId, setPosterCouponId] = useState<string | null>(null)
  const [viewOrderId, setViewOrderId] = useState<string | null>(null)

  const { data: templatesData } = useQuery(CouponTemplatesDocument, {
    variables: { siteId },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })
  const templates = templatesData?.couponTemplates ?? []

  const {
    data: couponsData,
    loading: couponsLoading,
    refetch: refetchCoupons,
  } = useQuery(UserCouponsDocument, {
    variables: { siteId, userId: selectedUser?.id ?? null },
    skip: !searched,
    fetchPolicy: 'network-only',
  })
  const userCoupons = couponsData?.userCoupons ?? []

  const [voidCoupon] = useMutation(VoidCouponDocument, {
    update(cache, { data }, { variables }) {
      if (data?.voidCoupon?.__typename !== 'GraphqlSuccess') return
      cache.writeFragment({
        id: `Coupon:${variables?.couponId}`,
        fragment: gql`
          fragment VoidedCoupon on Coupon {
            status
          }
        `,
        data: { status: CouponStatus.Voided },
      })
    },
  })

  const handleVoid = (couponId: string) => {
    message.prompt('此操作不可撤销，该优惠券将被作废。', {
      title: '确认作废优惠券',
      noLabel: '返回',
      yesLabel: '确认作废',
      onYesButton: async () => {
        const { data } = await voidCoupon({ variables: { couponId: couponId } })
        const result = data?.voidCoupon
        if (result?.__typename === 'GraphqlRequestError') {
          message.error(result.message ?? '作废失败')
          return
        }
        message.success('优惠券已作废', { autoHide: 2000 })
      },
    })
  }

  const rows = [...userCoupons]
    .sort((a, b) => {
      const aActive = a.status === CouponStatus.Active ? 0 : 1
      const bActive = b.status === CouponStatus.Active ? 0 : 1
      if (aActive !== bActive) return aActive - bActive
      return b.issuedAt < a.issuedAt ? -1 : b.issuedAt > a.issuedAt ? 1 : 0
    })
    .map((c) => ({
      id: c.id,
      couponId: c.id.slice(-8),
      templateName: c.template.name,
      category: COUPON_CATEGORY_LABEL[c.template.category] ?? c.template.category,
      sport: c.template.sport
        ? (SPORT_LABEL[c.template.sport] ?? c.template.sport)
        : '不限',
      rawStatus: c.status,
      status: STATUS_LABEL[c.status] ?? c.status,
      isUnbound: selectedUser === null,
      onVoid: handleVoid,
      onPoster: setPosterCouponId,
      onViewOrder: setViewOrderId,
      orderId: c.orderId ?? null,
      issuedAt: dateFormat(c.issuedAt, { format: 'yyyy-MM-dd HH:mm:ss' }),
      issuerName: c.issuer?.name ?? '—',
      expiresAt: c.expiresAt,
      discountedAmount:
        c.discountedAmount != null
          ? currency(c.discountedAmount)
          : c.template.discountAmount != null
            ? currency(c.template.discountAmount)
            : '—',
      redeemedAt: c.redeemedAt
        ? dateFormat(c.redeemedAt, { format: 'yyyy-MM-dd HH:mm:ss' })
        : '—',
    }))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        用户优惠券
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexShrink: 0 }}>
        <UserSelector
          siteId={siteId}
          value={selectedUser}
          onChange={(user) => {
            setSelectedUser(user)
            if (user) setSearched(true)
          }}
          onClear={() => setSearched(false)}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<SearchIcon />}
          onClick={async () => {
            if (searched) await refetchCoupons()
            else setSearched(true)
          }}
        >
          搜索
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={templates.length === 0}
          onClick={() => setIssueDialogOpen(true)}
        >
          发放优惠券
        </Button>
      </Box>

      {searched && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Typography variant="h6" sx={{ mb: 1, flexShrink: 0 }}>
            {selectedUser ? `${selectedUser.name} 的优惠券` : '未绑定优惠券'}
          </Typography>
          <DataGridPro
            rows={rows}
            columns={columns}
            loading={couponsLoading}
            hideFooter
            disableRowSelectionOnClick
            sx={{ flex: 1, minHeight: 0 }}
          />
        </Box>
      )}

      {issueDialogOpen && (
        <IssueCouponDialog
          user={selectedUser}
          templates={templates}
          onClose={() => setIssueDialogOpen(false)}
          onIssued={() => {
            if (searched) refetchCoupons()
          }}
        />
      )}

      {viewOrderId && (
        <OrderDialog orderId={viewOrderId} onClose={() => setViewOrderId(null)} />
      )}

      {posterCouponId &&
        (() => {
          const c = userCoupons.find((x) => x.id === posterCouponId)
          if (!c) return null
          return (
            <CouponPosterDialog
              onClose={() => setPosterCouponId(null)}
              source={{
                kind: 'coupon',
                id: posterCouponId,
                siteId,
                template: {
                  name: c.template.name,
                  category: c.template.category,
                  sport: c.template.sport,
                  discountAmount: c.template.discountAmount,
                },
              }}
            />
          )
        })()}
    </Box>
  )
}
